import { useState, useCallback, useRef } from "react"
import { fetchLeads, fetchLeadCounts, createLead, updateLead, deleteLead, importLeads } from "../api/leadsApi"

// ── useLeadCounts ─────────────────────────────────────────────────────────────
// Per-scope tab totals (all / my / team / unassigned / deleted / duplicate).
export function useLeadCounts() {
  const [counts, setCounts] = useState(null)

  const refresh = useCallback(async () => {
    try {
      setCounts(await fetchLeadCounts())
    } catch {
      // Counts are non-critical chrome; leave stale on failure.
    }
  }, [])

  return { counts, refresh }
}

// ── useLeads ─────────────────────────────────────────────────────────────────
// Fetches the list with pagination/filter support.
export function useLeads() {
  const [data, setData]       = useState(null)   // { leads, meta }
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState(null)
  const abortRef              = useRef(null)

  const fetch = useCallback(async (filters = {}) => {
    if (abortRef.current) abortRef.current.abort()
    abortRef.current = new AbortController()

    setLoading(true)
    setError(null)
    try {
      const result = await fetchLeads(filters)
      setData(result)
    } catch (err) {
      if (err.name !== "AbortError") setError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  const refresh = useCallback((filters) => fetch(filters), [fetch])

  return { data, loading, error, fetch, refresh }
}

// ── useCreateLead ─────────────────────────────────────────────────────────────
export function useCreateLead() {
  const [status, setStatus] = useState("idle")
  const [error, setError]   = useState(null)

  const mutate = useCallback(async (payload) => {
    setStatus("pending")
    setError(null)
    try {
      const result = await createLead(payload)
      setStatus("success")
      return result
    } catch (err) {
      const msg = err.response?.data?.errors || [err.message || "Something went wrong"]
      setError(msg)
      setStatus("error")
      throw msg
    }
  }, [])

  return { mutate, status, error, isPending: status === "pending", reset: () => setStatus("idle") }
}

// ── useUpdateLead ─────────────────────────────────────────────────────────────
export function useUpdateLead() {
  const [status, setStatus] = useState("idle")
  const [error, setError]   = useState(null)

  const mutate = useCallback(async (id, payload) => {
    setStatus("pending")
    setError(null)
    try {
      const result = await updateLead(id, payload)
      setStatus("success")
      return result
    } catch (err) {
      const msg = err.response?.data?.errors || [err.message || "Something went wrong"]
      setError(msg)
      setStatus("error")
      throw msg
    }
  }, [])

  return { mutate, status, error, isPending: status === "pending", reset: () => setStatus("idle") }
}

// ── useDeleteLead ─────────────────────────────────────────────────────────────
export function useDeleteLead() {
  const [status, setStatus] = useState("idle")

  const mutate = useCallback(async (id) => {
    setStatus("pending")
    try {
      await deleteLead(id)
      setStatus("success")
    } catch (err) {
      setStatus("error")
      throw err
    }
  }, [])

  return { mutate, isPending: status === "pending", status, reset: () => setStatus("idle") }
}

// ── useImportLeads ───────────────────────────────────────────────────────────
export function useImportLeads() {
  const [status, setStatus] = useState("idle")
  const [result, setResult] = useState(null)
  const [error, setError]   = useState(null)

  const mutate = useCallback(async (rows) => {
    setStatus("pending")
    setError(null)
    try {
      const data = await importLeads(rows)
      setResult(data)
      setStatus("success")
      return data
    } catch (err) {
      setError(err.message || "Something went wrong")
      setStatus("error")
      throw err
    }
  }, [])

  return {
    mutate, result, error, status,
    isPending: status === "pending",
    isSuccess: status === "success",
    isError:   status === "error",
    reset: () => { setStatus("idle"); setResult(null); setError(null) },
  }
}
